import React, { useEffect, useRef } from 'react';
import hljs from 'highlight.js/lib/core';

// Import common languages
import javascript from 'highlight.js/lib/languages/javascript';
import typescript from 'highlight.js/lib/languages/typescript';
import python from 'highlight.js/lib/languages/python';
import html from 'highlight.js/lib/languages/xml';
import css from 'highlight.js/lib/languages/css';
import json from 'highlight.js/lib/languages/json';
import bash from 'highlight.js/lib/languages/bash';
import sql from 'highlight.js/lib/languages/sql';
import java from 'highlight.js/lib/languages/java';
import csharp from 'highlight.js/lib/languages/csharp';
import php from 'highlight.js/lib/languages/php';
import ruby from 'highlight.js/lib/languages/ruby';
import go from 'highlight.js/lib/languages/go';
import rust from 'highlight.js/lib/languages/rust';
import markdown from 'highlight.js/lib/languages/markdown';
import yaml from 'highlight.js/lib/languages/yaml';

// Register languages
hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('js', javascript);
hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('ts', typescript);
hljs.registerLanguage('python', python);
hljs.registerLanguage('py', python);
hljs.registerLanguage('html', html);
hljs.registerLanguage('xml', html);
hljs.registerLanguage('css', css);
hljs.registerLanguage('json', json);
hljs.registerLanguage('bash', bash);
hljs.registerLanguage('shell', bash);
hljs.registerLanguage('sql', sql);
hljs.registerLanguage('java', java);
hljs.registerLanguage('csharp', csharp);
hljs.registerLanguage('cs', csharp);
hljs.registerLanguage('php', php);
hljs.registerLanguage('ruby', ruby);
hljs.registerLanguage('rb', ruby);
hljs.registerLanguage('go', go);
hljs.registerLanguage('rust', rust);
hljs.registerLanguage('markdown', markdown);
hljs.registerLanguage('md', markdown);
hljs.registerLanguage('yaml', yaml);
hljs.registerLanguage('yml', yaml);

// Theme options
export const CODE_THEMES = [
  { value: 'github-dark', label: 'GitHub Dark' },
  { value: 'monokai', label: 'Monokai' },
  { value: 'dracula', label: 'Dracula' },
  { value: 'nord', label: 'Nord' },
  { value: 'atom-one-dark', label: 'Atom One Dark' },
  { value: 'vs2015', label: 'VS 2015' },
  { value: 'github', label: 'GitHub Light' },
  { value: 'stackoverflow-light', label: 'Stack Overflow Light' },
];

// Language options
export const CODE_LANGUAGES = [
  { value: 'javascript', label: 'JavaScript' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'python', label: 'Python' },
  { value: 'html', label: 'HTML' },
  { value: 'css', label: 'CSS' },
  { value: 'json', label: 'JSON' },
  { value: 'bash', label: 'Bash/Shell' },
  { value: 'sql', label: 'SQL' },
  { value: 'java', label: 'Java' },
  { value: 'csharp', label: 'C#' },
  { value: 'php', label: 'PHP' },
  { value: 'ruby', label: 'Ruby' },
  { value: 'go', label: 'Go' },
  { value: 'rust', label: 'Rust' },
  { value: 'markdown', label: 'Markdown' },
  { value: 'yaml', label: 'YAML' },
];

const CodeBlock = ({ code, language = 'javascript', theme = 'github-dark', showLineNumbers = true }) => {
  const codeRef = useRef(null);

  useEffect(() => {
    if (codeRef.current && code) {
      try {
        const result = hljs.highlight(code, { 
          language: language || 'plaintext',
          ignoreIllegals: true 
        });
        codeRef.current.innerHTML = result.value;
      } catch (e) {
        // If language not found, use auto-detection or plain text
        try {
          const result = hljs.highlightAuto(code);
          codeRef.current.innerHTML = result.value;
        } catch {
          codeRef.current.textContent = code;
        }
      }
    }
  }, [code, language]);

  const lines = code ? code.split('\n') : [];

  // Theme-based styles
  const getThemeStyles = () => {
    const themes = {
      'github-dark': { bg: '#0d1117', text: '#c9d1d9', border: '#30363d', lineNum: '#484f58' },
      'monokai': { bg: '#272822', text: '#f8f8f2', border: '#3e3d32', lineNum: '#75715e' },
      'dracula': { bg: '#282a36', text: '#f8f8f2', border: '#44475a', lineNum: '#6272a4' },
      'nord': { bg: '#2e3440', text: '#d8dee9', border: '#3b4252', lineNum: '#4c566a' },
      'atom-one-dark': { bg: '#282c34', text: '#abb2bf', border: '#3e4451', lineNum: '#636d83' },
      'vs2015': { bg: '#1e1e1e', text: '#dcdcdc', border: '#2d2d2d', lineNum: '#858585' },
      'github': { bg: '#ffffff', text: '#24292e', border: '#e1e4e8', lineNum: '#959da5' },
      'stackoverflow-light': { bg: '#f6f6f6', text: '#2f3337', border: '#e0e0e0', lineNum: '#9a9a9a' },
    };
    return themes[theme] || themes['github-dark'];
  };

  const themeStyles = getThemeStyles();

  return (
    <div 
      className="rounded-lg overflow-hidden font-mono text-sm"
      style={{ 
        backgroundColor: themeStyles.bg,
        border: `1px solid ${themeStyles.border}`
      }}
    >
      {/* Header with language badge */}
      <div 
        className="flex items-center justify-between px-4 py-2"
        style={{ 
          borderBottom: `1px solid ${themeStyles.border}`,
          backgroundColor: themeStyles.border 
        }}
      >
        <span 
          className="text-xs font-medium uppercase tracking-wide"
          style={{ color: themeStyles.lineNum }}
        >
          {language || 'code'}
        </span>
        <button
          onClick={() => navigator.clipboard.writeText(code)}
          className="text-xs px-2 py-1 rounded hover:bg-black/20 transition-colors"
          style={{ color: themeStyles.lineNum }}
          title="Copy code"
        >
          Copy
        </button>
      </div>

      {/* Code content */}
      <div className="overflow-x-auto">
        <div className="flex min-w-full">
          {/* Line numbers */}
          {showLineNumbers && lines.length > 0 && (
            <div 
              className="flex-shrink-0 text-right pr-4 pl-4 py-4 select-none"
              style={{ 
                color: themeStyles.lineNum,
                borderRight: `1px solid ${themeStyles.border}`,
                backgroundColor: `${themeStyles.bg}cc`
              }}
            >
              {lines.map((_, i) => (
                <div key={i} className="leading-6">{i + 1}</div>
              ))}
            </div>
          )}

          {/* Code */}
          <pre className="flex-1 p-4 m-0 overflow-x-auto" style={{ color: themeStyles.text }}>
            <code ref={codeRef} className={`hljs language-${language}`}>
              {code || '// Enter your code here...'}
            </code>
          </pre>
        </div>
      </div>
    </div>
  );
};

export default CodeBlock;
